import { MouseEvent } from 'react';
import {
  resolveValue,
  Toast as HotToast,
  toast,
  ToastBar,
  Toaster as HotToaster,
  ToastType,
} from 'react-hot-toast';
import { useUpdateEffect } from 'react-use';
import { twJoin } from 'tailwind-merge';

import {
  Icon,
  IconButton,
  Portal,
  type Ryecon,
  RyeconCheckmarkOutline,
  RyeconClose,
  RyeconErrorOutline,
  RyeconSpinner,
  Sizes,
} from '@dbt-labs/sourdough';

import { useRawParams } from '@/hooks/params';

const iconMap: Record<ToastType, Ryecon | undefined> = {
  success: RyeconCheckmarkOutline,
  error: RyeconErrorOutline,
  loading: RyeconSpinner,
  blank: undefined,
  custom: undefined,
};
const iconColorMap = {
  error: 'text-fgDanger',
  success: 'text-fgSuccess',
  loading: 'text-fgDecorative',
};
const onCloseHandler = (toastId?: string) => (e: MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
  toast.dismiss(toastId);
};

export const Toaster = () => {
  const { accountId, projectId } = useRawParams();

  useUpdateEffect(() => {
    toast.dismiss();
  }, [accountId, projectId]);

  return (
    <Portal>
      <HotToaster
        position="bottom-right"
        containerStyle={{ zIndex: 50 }}
        toastOptions={{
          className: 'min-w-[400px] bg-bgMain text-fgMain',
          style: { paddingTop: '0px', paddingBottom: '0px' },
          error: {
            duration: Infinity,
          },
        }}
      >
        {Toast}
      </HotToaster>
    </Portal>
  );
};

export const Toast = (toast: HotToast) => {
  const icon = iconMap[toast.type];
  return (
    <ToastBar
      toast={toast}
      position="bottom-right"
      style={{ ...toast.style, justifyItems: 'left' }}
    >
      {/* TODO: We should use the toast component from sourdough */}
      {() => (
        <div
          className="flex w-full items-center justify-center py-4"
          data-testid="toast"
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <div
            style={{ borderRadius: '50%' }}
            className={twJoin(
              iconColorMap[toast.type as keyof typeof iconColorMap],
              toast.type === 'loading' && 'motion-safe:animate-spin',
              'flex flex-col items-center align-middle',
            )}
          >
            {icon && <Icon className="h-4 w-4" ryecon={icon} alt={toast.type} />}
          </div>

          <div className="ml-2 grow">{resolveValue(toast.message, toast)}</div>
          {toast.type === 'error' && (
            <IconButton
              className="ml-4"
              size={Sizes.sm}
              ryecon={RyeconClose}
              onClick={onCloseHandler(toast.id)}
              label="dismiss"
            />
          )}
        </div>
      )}
    </ToastBar>
  );
};
